import type { ImportBatch, ImportBatchView, ImportCoverage, ImportRow } from "@agency/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ArrowLeft, CheckCircle2, Play, Save, Upload } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { callAction } from "../api";
import { useToast } from "../components/Toast";
import { Badge, Button, Card, CardHeader, ErrorState, Input, LoadingState, Select, cn } from "../components/ui";
import { useLocale, useT } from "../i18n";
import { shortDate } from "../theme";
import { statusTone } from "./Import";

type Step = "preview" | "map" | "validate" | "review" | "import" | "done";
const STEPS: Step[] = ["preview", "map", "validate", "review", "import", "done"];

/** Target fields in display order with the entity they belong to and whether they identify a record. */
const TARGETS: Array<{ key: string; entity: "customer" | "policy"; required?: boolean }> = [
  { key: "sourceCustomerId", entity: "customer" }, { key: "firstName", entity: "customer", required: true }, { key: "lastName", entity: "customer", required: true }, { key: "fullName", entity: "customer" }, { key: "businessName", entity: "customer" },
  { key: "email", entity: "customer" }, { key: "phone", entity: "customer" }, { key: "phone2", entity: "customer" }, { key: "address1", entity: "customer" }, { key: "address2", entity: "customer" }, { key: "city", entity: "customer" }, { key: "state", entity: "customer" }, { key: "zip", entity: "customer" },
  { key: "dob", entity: "customer" }, { key: "preferredLanguage", entity: "customer" }, { key: "customerStatus", entity: "customer" }, { key: "customerNotes", entity: "customer" }, { key: "tags", entity: "customer" },
  { key: "sourcePolicyId", entity: "policy" }, { key: "carrier", entity: "policy", required: true }, { key: "policyNumber", entity: "policy", required: true }, { key: "line", entity: "policy" }, { key: "policyStatus", entity: "policy" },
  { key: "effectiveDate", entity: "policy", required: true }, { key: "expirationDate", entity: "policy", required: true }, { key: "premium", entity: "policy" }, { key: "priorPremium", entity: "policy" }, { key: "billing", entity: "policy" }, { key: "producer", entity: "policy" }, { key: "policyNotes", entity: "policy" },
];
const LABELS: Record<string, { en: string; es: string }> = {
  sourceCustomerId: { en: "Source customer ID", es: "ID de cliente (origen)" }, firstName: { en: "First name", es: "Nombre" }, lastName: { en: "Last name", es: "Apellido" }, fullName: { en: "Full name (one column)", es: "Nombre completo (una columna)" }, businessName: { en: "Business / company", es: "Negocio / empresa" },
  email: { en: "Email", es: "Correo" }, phone: { en: "Phone (mobile)", es: "Teléfono (celular)" }, phone2: { en: "Phone (other)", es: "Teléfono (otro)" }, address1: { en: "Address line 1", es: "Dirección línea 1" }, address2: { en: "Address line 2", es: "Dirección línea 2" }, city: { en: "City", es: "Ciudad" }, state: { en: "State", es: "Estado" }, zip: { en: "ZIP", es: "Código postal" },
  dob: { en: "Date of birth", es: "Fecha de nacimiento" }, preferredLanguage: { en: "Preferred language", es: "Idioma preferido" }, customerStatus: { en: "Customer status", es: "Estado del cliente" }, customerNotes: { en: "Customer notes", es: "Notas del cliente" }, tags: { en: "Tags", es: "Etiquetas" },
  sourcePolicyId: { en: "Source policy ID", es: "ID de póliza (origen)" }, carrier: { en: "Carrier", es: "Aseguradora" }, policyNumber: { en: "Policy number", es: "Número de póliza" }, line: { en: "Line of business", es: "Línea" }, policyStatus: { en: "Policy status", es: "Estado de la póliza" },
  effectiveDate: { en: "Effective date", es: "Fecha de inicio" }, expirationDate: { en: "Expiration date", es: "Fecha de vencimiento" }, premium: { en: "Premium", es: "Prima" }, priorPremium: { en: "Prior term premium", es: "Prima de la vigencia anterior" }, billing: { en: "Billing", es: "Facturación" }, producer: { en: "Producer", es: "Productor" }, policyNotes: { en: "Policy notes", es: "Notas de la póliza" },
};

function stepOf(b: ImportBatch): Step {
  if (b.status === "imported") return "done";
  if (b.status === "validated") return "review";
  if (b.status === "mapped") return "map";
  return "preview";
}

export function ImportBatchScreen() {
  const { batchId = "" } = useParams();
  const t = useT();
  const { locale } = useLocale();
  const qc = useQueryClient();
  const { toast } = useToast();
  const [outcome, setOutcome] = useState<ImportRow["outcome"] | "">("");
  const key = ["import", batchId, outcome];
  const q = useQuery({ queryKey: key, queryFn: async () => (await callAction<"agency.import.get", ImportBatchView>("agency.import.get", { batchId, outcome: outcome || undefined, limit: 2000 })).result, enabled: !!batchId });
  const [step, setStep] = useState<Step | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [dateFormat, setDateFormat] = useState<"auto" | "MDY" | "DMY" | "YMD">("auto");
  const [carrierDefault, setCarrierDefault] = useState("");
  const [saveName, setSaveName] = useState("");
  const [pickMapping, setPickMapping] = useState("");

  useEffect(() => {
    if (!q.data) return;
    const b = q.data.batch;
    setFields(Object.keys(b.fields).length ? b.fields : q.data.suggestedFields);
    setDateFormat(b.options.dateFormat ?? "auto");
    setCarrierDefault(b.options.carrierDefault ?? "");
    setStep((s) => s ?? stepOf(b));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q.data?.batch.id, q.data?.batch.status]);

  const refresh = () => { qc.invalidateQueries({ queryKey: ["import", batchId] }); qc.invalidateQueries({ queryKey: ["imports"] }); };
  const onErr = (e: unknown) => toast({ title: t("common.error"), body: (e as Error).message, tone: "error" });

  const map = useMutation({
    mutationFn: (v: { saveAs?: { name: string } }) => callAction<"agency.import.map", ImportBatchView>("agency.import.map", { batchId, fields: Object.fromEntries(Object.entries(fields).filter(([, c]) => c)), options: { dateFormat, carrierDefault: carrierDefault || undefined }, saveAs: v.saveAs, mappingId: pickMapping || undefined }),
    onSuccess: () => { refresh(); setStep("validate"); },
    onError: onErr,
  });
  const validate = useMutation({
    mutationFn: () => callAction<"agency.import.validate", ImportBatchView>("agency.import.validate", { batchId }),
    onSuccess: () => { refresh(); setStep("review"); },
    onError: onErr,
  });
  const commit = useMutation({
    mutationFn: () => callAction<"agency.import.commit", ImportBatchView>("agency.import.commit", { batchId }),
    onSuccess: (r) => { refresh(); setStep("done"); qc.invalidateQueries({ queryKey: ["customers"] }); qc.invalidateQueries({ queryKey: ["today"] }); toast({ title: t("import.done.title"), body: `${r.result.batch.result?.created.customers ?? 0} / ${r.result.batch.result?.created.policies ?? 0}`, tone: "success" }); },
    onError: onErr,
  });

  const missingRequired = useMemo(() => {
    const hasName = !!(fields.firstName || fields.lastName || fields.fullName || fields.businessName);
    const hasPolicyCols = !!(fields.policyNumber || fields.sourcePolicyId);
    const miss: string[] = [];
    if (!hasName) miss.push("name");
    if (hasPolicyCols) for (const k of ["carrier", "policyNumber", "effectiveDate", "expirationDate"]) if (!fields[k] && !(k === "carrier" && carrierDefault)) miss.push(k);
    return miss;
  }, [fields, carrierDefault]);

  if (q.isLoading || !step) return <LoadingState />;
  if (q.isError || !q.data) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  const { batch: b, rows, mappings } = q.data;
  const curIdx = STEPS.indexOf(step);
  const doneIdx = STEPS.indexOf(stepOf(b));
  const lbl = (k: string) => LABELS[k]?.[locale] ?? k;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Link to="/import" className="inline-flex min-h-[40px] items-center gap-1 text-sm font-semibold text-primary-dark hover:underline"><ArrowLeft className="h-4 w-4" />{t("import.back")}</Link>
        <div className="mr-auto" />
        <Badge tone={statusTone(b.status)}>{t(`import.status.${b.status}`)}</Badge>
        <Badge>{t(`import.origin.${b.origin}`)}</Badge>
      </div>
      <div>
        <h1 className="text-2xl font-bold truncate">{b.filename}</h1>
        <p className="text-sm text-ink/60"><span className="font-mono">{b.sourceSystem}</span> · {b.rowCount} {t("import.rows")} · {b.columns.length} {t("import.columns")} · {shortDate(b.createdAt, locale)} · <span className="font-mono text-xs">sha256 {b.checksumSha256.slice(0, 12)}…</span></p>
      </div>
      {b.duplicateOfBatchId ? <div className="rounded-2xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 inline-flex items-start gap-2"><AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />{t("import.duplicate").replace("{id}", b.duplicateOfBatchId)}</div> : null}

      <ol className="flex flex-wrap gap-1">
        {STEPS.map((s, i) => (
          <li key={s}>
            <button onClick={() => i <= Math.max(doneIdx, curIdx) + (b.status === "imported" ? 0 : 1) && setStep(s)} disabled={b.status === "imported" ? false : i > doneIdx + 1} className={cn("min-h-[40px] rounded-xl px-3 text-sm font-semibold border", s === step ? "bg-ink text-white border-ink" : i <= doneIdx ? "bg-primary/10 text-primary-dark border-primary/30" : "bg-white text-ink/50 border-ink/10 disabled:opacity-50")}>
              {i + 1}. {t(`import.step.${s}`)}
            </button>
          </li>
        ))}
      </ol>

      {step === "preview" ? (
        <Card>
          <CardHeader title={t("import.preview.title")} subtitle={t("import.preview.sub")} action={<Button size="sm" onClick={() => setStep("map")}>{t("import.step.map")} →</Button>} />
          <div className="overflow-x-auto px-2 pb-4">
            <table className="text-xs whitespace-nowrap">
              <thead className="text-left text-ink/55"><tr>{b.columns.map((c) => <th key={c} className="px-2 py-1 border-b border-ink/10">{c}</th>)}</tr></thead>
              <tbody>{b.preview.map((r, i) => <tr key={i} className="border-b border-ink/5">{b.columns.map((c) => <td key={c} className="px-2 py-1 max-w-[220px] truncate">{r[c]}</td>)}</tr>)}</tbody>
            </table>
          </div>
        </Card>
      ) : null}

      {step === "map" ? (
        <Card>
          <CardHeader title={t("import.map.title")} subtitle={t("import.map.sub")} />
          <div className="px-5 pb-5 space-y-4">
            <div className="grid gap-3 sm:grid-cols-3">
              <Select label={t("import.map.saved")} value={pickMapping} onChange={(e) => { const id = e.target.value; setPickMapping(id); const m = mappings.find((x) => x.id === id); if (m) { setFields(m.fields); setDateFormat(m.options.dateFormat ?? "auto"); setCarrierDefault(m.options.carrierDefault ?? ""); } else setFields(q.data!.suggestedFields); }}>
                <option value="">{t("import.map.useSuggested")}</option>
                {mappings.map((m) => <option key={m.id} value={m.id}>{m.name}{m.builtIn ? ` (${t("import.mapping.builtin")})` : ""} · {m.sourceSystem}</option>)}
              </Select>
              <Select label={t("import.map.dateFormat")} value={dateFormat} onChange={(e) => setDateFormat(e.target.value as typeof dateFormat)}>
                {(["auto", "MDY", "DMY", "YMD"] as const).map((f) => <option key={f} value={f}>{t(`import.map.date.${f}`)}</option>)}
              </Select>
              <Input label={t("import.map.carrierDefault")} value={carrierDefault} onChange={(e) => setCarrierDefault(e.target.value)} />
            </div>
            <div className="grid gap-x-6 gap-y-2 md:grid-cols-2">
              {(["customer", "policy"] as const).map((entity) => (
                <div key={entity}>
                  <p className="text-xs font-bold uppercase tracking-wide text-ink/50 mb-1">{entity === "customer" ? t("import.coverage.customers") : t("import.coverage.policies")}</p>
                  {TARGETS.filter((x) => x.entity === entity).map((tf) => {
                    const req = tf.required && (entity === "customer" ? !(fields.fullName || fields.businessName) && (tf.key === "firstName" || tf.key === "lastName") : !!(fields.policyNumber || fields.sourcePolicyId));
                    const miss = req && !fields[tf.key] && !(tf.key === "carrier" && carrierDefault);
                    return (
                      <label key={tf.key} className={cn("grid grid-cols-[1fr_1fr] items-center gap-2 py-1 border-b border-ink/5", miss && "bg-amber-50")}>
                        <span className="text-sm">{lbl(tf.key)}{req ? <span className="ml-1 text-[10px] uppercase text-amber-700 font-bold">{t("import.map.required")}</span> : null}</span>
                        <select className="min-h-[36px] rounded-lg border border-ink/15 bg-white px-2 text-sm" value={fields[tf.key] ?? ""} onChange={(e) => setFields({ ...fields, [tf.key]: e.target.value })}>
                          <option value="">{t("import.map.unmapped")}</option>
                          {b.columns.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </label>
                    );
                  })}
                </div>
              ))}
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
              <Input className="sm:max-w-xs" label={t("import.map.saveAs")} placeholder={t("import.map.saveName")} value={saveName} onChange={(e) => setSaveName(e.target.value)} />
              <Button variant="outline" disabled={!saveName.trim() || missingRequired.length > 0} loading={map.isPending} onClick={() => map.mutate({ saveAs: { name: saveName.trim() } })}><Save className="h-4 w-4" />{t("import.map.save")}</Button>
              <div className="flex-1" />
              <Button disabled={missingRequired.length > 0} loading={map.isPending} onClick={() => map.mutate({})}>{t("import.map.apply")} →</Button>
            </div>
            {missingRequired.length ? <p className="text-sm text-amber-800">{t("import.map.required")}: {missingRequired.map((k) => (k === "name" ? t("common.name") : lbl(k))).join(", ")}</p> : null}
          </div>
        </Card>
      ) : null}

      {step === "validate" ? (
        <Card className="p-5 space-y-3">
          <p className="font-semibold">{t("import.step.validate")}</p>
          <p className="text-sm text-ink/60">{t("import.validate.sub")}</p>
          <Button onClick={() => validate.mutate()} loading={validate.isPending}><Play className="h-4 w-4" />{validate.isPending ? t("import.validate.running") : t("import.validate.run")}</Button>
        </Card>
      ) : null}

      {step === "review" || step === "import" ? (
        <>
          {b.validation ? <Summary t={t} counts={{ ...b.validation, unchanged: 0 }} title={t("import.validate.title")} sub={t("import.validate.sub")} /> : null}
          {b.coverage ? <Coverage t={t} c={b.coverage} /> : null}
          {b.validation?.warnings.length ? (
            <Card className="p-4">
              <p className="font-semibold text-sm mb-1">{t("import.warnings")} ({b.validation.warnings.length})</p>
              <ul className="list-disc pl-5 text-sm text-ink/70 max-h-40 overflow-y-auto">{b.validation.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
            </Card>
          ) : null}
          <RowTable t={t} rows={rows} outcome={outcome} setOutcome={setOutcome} />
          <Card className="p-4 flex flex-col gap-2 sm:flex-row sm:items-center">
            <p className="text-sm text-ink/60 flex-1">{b.validation?.exceptions ? t("import.commit.blocked") : ""}</p>
            <Button variant="outline" onClick={() => validate.mutate()} loading={validate.isPending}>{t("import.revalidate")}</Button>
            <Button size="lg" onClick={() => commit.mutate()} loading={commit.isPending} disabled={b.status !== "validated"}><Upload className="h-4 w-4" />{commit.isPending ? t("import.committing") : t("import.commit").replace("{n}", String(b.rowCount - (b.validation?.exceptions ?? 0) - (b.validation?.skips ?? 0)))}</Button>
          </Card>
        </>
      ) : null}

      {step === "done" && b.result ? (
        <>
          <Card className="p-5">
            <p className="text-lg font-bold inline-flex items-center gap-2 text-primary-dark"><CheckCircle2 className="h-5 w-5" />{t("import.done.title")}</p>
            <p className="text-sm text-ink/60">{t("import.done.sub")} · {shortDate(b.result.importedAt, locale)}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Badge tone="green">{t("import.counts.newCustomers")}: {b.result.created.customers}</Badge>
              <Badge tone="green">{t("import.counts.newPolicies")}: {b.result.created.policies}</Badge>
              <Badge tone="blue">{t("import.counts.updCustomers")}: {b.result.updated.customers}</Badge>
              <Badge tone="blue">{t("import.counts.updPolicies")}: {b.result.updated.policies}</Badge>
              <Badge>{b.result.created.notes} {t("import.done.notes")}</Badge>
              <Badge tone="amber">{t("import.counts.skips")}: {b.result.skips}</Badge>
              <Badge tone={b.result.exceptions ? "red" : "neutral"}>{t("import.counts.exceptions")}: {b.result.exceptions}</Badge>
            </div>
            <p className="mt-2 text-sm text-ink/70">{b.result.customerIds.length} {t("import.done.customers")} · {b.result.policyIds.length} {t("import.done.policies")}</p>
            <Link to="/customers" className="mt-3 inline-flex min-h-[40px] items-center font-semibold text-primary-dark hover:underline">{t("import.done.openCustomers")} →</Link>
          </Card>
          {b.coverage ? <Coverage t={t} c={b.coverage} /> : null}
          <RowTable t={t} rows={rows} outcome={outcome} setOutcome={setOutcome} />
        </>
      ) : null}
    </div>
  );
}

function Summary({ t, counts, title, sub }: { t: (k: string) => string; counts: { creates: { customers: number; policies: number }; updates: { customers: number; policies: number }; skips: number; exceptions: number; unchanged: number }; title: string; sub: string }) {
  return (
    <Card className="p-5">
      <p className="font-bold">{title}</p>
      <p className="text-sm text-ink/60 mb-3">{sub}</p>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
        <Stat label={t("import.counts.newCustomers")} value={counts.creates.customers} tone="green" />
        <Stat label={t("import.counts.newPolicies")} value={counts.creates.policies} tone="green" />
        <Stat label={t("import.counts.updCustomers")} value={counts.updates.customers} tone="blue" />
        <Stat label={t("import.counts.updPolicies")} value={counts.updates.policies} tone="blue" />
        <Stat label={t("import.counts.skips")} value={counts.skips} tone="amber" />
        <Stat label={t("import.counts.exceptions")} value={counts.exceptions} tone={counts.exceptions ? "red" : "neutral"} />
      </div>
    </Card>
  );
}

function Stat({ label, value, tone }: { label: string; value: number; tone: "green" | "blue" | "amber" | "red" | "neutral" }) {
  const tones = { green: "bg-primary/10 text-primary-dark", blue: "bg-blue-50 text-blue-700", amber: "bg-accent/20 text-amber-800", red: "bg-red-50 text-red-700", neutral: "bg-ink/5 text-ink/70" };
  return <div className={cn("rounded-2xl p-3", tones[tone])}><p className="text-2xl font-bold leading-none">{value}</p><p className="text-xs mt-1">{label}</p></div>;
}

function Coverage({ t, c }: { t: (k: string) => string; c: ImportCoverage }) {
  const keys = ["customers", "policies", "notes", "attachments", "priorTerms", "history"] as const;
  return (
    <Card>
      <CardHeader title={t("import.coverage.title")} subtitle={t("import.coverage.sub")} />
      <ul className="px-5 pb-5 grid gap-2 sm:grid-cols-3">
        {keys.map((k) => (
          <li key={k} className={cn("rounded-2xl border p-3", c[k].present ? "border-primary/30 bg-primary/5" : "border-ink/10 bg-white")}>
            <p className="font-semibold text-sm">{t(`import.coverage.${k}`)}</p>
            <p className={cn("text-xs", c[k].present ? "text-primary-dark" : "text-ink/50")}>{c[k].present ? `${c[k].count} ${t("import.coverage.present")}` : t("import.coverage.absent")}</p>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function RowTable({ t, rows, outcome, setOutcome }: { t: (k: string) => string; rows: ImportRow[]; outcome: ImportRow["outcome"] | ""; setOutcome: (o: ImportRow["outcome"] | "") => void }) {
  const tone = (o: ImportRow["outcome"]) => (o === "create" ? "green" : o === "update" ? "blue" : o === "skip_duplicate" ? "amber" : o === "exception" ? "red" : "neutral");
  return (
    <Card>
      <CardHeader title={`${t("import.rows.title")} (${rows.length})`} action={
        <Select aria-label={t("import.rows.title")} value={outcome} onChange={(e) => setOutcome(e.target.value as ImportRow["outcome"] | "")} className="w-44">
          <option value="">{t("import.rows.all")}</option>
          {(["create", "update", "unchanged", "skip_duplicate", "exception"] as const).map((o) => <option key={o} value={o}>{t(`import.outcome.${o}`)}</option>)}
        </Select>
      } />
      <div className="overflow-x-auto max-h-[60vh]">
        <table className="w-full text-sm">
          <thead className="text-left text-ink/55 sticky top-0 bg-white">
            <tr><th className="px-4 py-2">{t("import.rows.row")}</th><th className="px-4 py-2">{t("common.status")}</th><th className="px-4 py-2">{t("import.rows.record")}</th><th className="px-4 py-2">{t("import.rows.reason")}</th></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-ink/5 align-top">
                <td className="px-4 py-2 font-mono text-xs">{r.row}</td>
                <td className="px-4 py-2"><Badge tone={tone(r.outcome)}>{t(`import.outcome.${r.outcome}`)}</Badge><span className="block text-[11px] text-ink/50">{r.entity}</span></td>
                <td className="px-4 py-2">
                  <span className="font-semibold">{r.data.name ?? ""}</span>
                  {r.data.policyNumber ? <span className="block text-xs text-ink/60">{r.data.carrier} {r.data.policyNumber} · {r.data.line} · {r.data.effectiveDate} → {r.data.expirationDate} · ${r.data.premium}</span> : null}
                  {r.customerId ? <Link to={`/customers/${r.customerId}`} className="block text-xs font-semibold text-primary-dark hover:underline">{t("import.openCustomer")} →</Link> : null}
                </td>
                <td className="px-4 py-2 text-xs text-ink/70">{r.reason ?? ""}{r.data.warnings ? <span className="block text-amber-800">{r.data.warnings}</span> : null}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
